import type { ComponentPropsWithRef } from "react";
import { cx, DISABLED, FOCUS_RING, TOUCH_TARGET } from "../styles";

/**
 * Text, long text, and numbers.
 *
 * The invalid treatment is the part with a reason behind it. A red border
 * alone is a colour-only signal, so an invalid field also carries a 3px
 * `--danger-bg` ring — a change in *shape* as well as in hue, which is what
 * somebody who cannot distinguish the two sees. `aria-invalid` carries it to a
 * screen reader, and {@link Field} sets that from the error it was given rather
 * than leaving it to each call site.
 */

const BASE =
	"w-full rounded-md border bg-bg-surface px-2.5 text-[13px] text-fg " +
	"placeholder:text-fg-subtle shadow-xs hover:border-fg-subtle " +
	"transition-[border-color,box-shadow] duration-150 motion-reduce:transition-none";

/**
 * The invalid treatment: a danger border and a soft ring of the same family.
 *
 * Driven off `aria-invalid` rather than a prop, so the attribute that assistive
 * technology reads and the colour a sighted person sees cannot disagree. There
 * is exactly one source of truth for "this field is wrong".
 */
const INVALID =
	"aria-[invalid]:border-danger-border aria-[invalid]:ring-3 aria-[invalid]:ring-danger-bg";

export type InputProps = ComponentPropsWithRef<"input">;

export function Input({ className, ...rest }: InputProps) {
	return (
		<input
			className={cx(
				BASE,
				"h-9 border-border-strong",
				FOCUS_RING,
				TOUCH_TARGET,
				INVALID,
				DISABLED,
				className,
			)}
			{...rest}
		/>
	);
}

export type TextareaProps = ComponentPropsWithRef<"textarea">;

export function Textarea({ className, rows = 3, ...rest }: TextareaProps) {
	return (
		<textarea
			rows={rows}
			className={cx(
				BASE,
				"min-h-20 border-border-strong py-2 leading-relaxed",
				FOCUS_RING,
				INVALID,
				DISABLED,
				className,
			)}
			{...rest}
		/>
	);
}

export interface NumberInputProps extends Omit<InputProps, "type"> {
	/**
	 * A suffix rendered inside the field: `kg`, `cm`, `%`.
	 *
	 * Inside rather than beside, because a unit that scrolls away from its
	 * number in a narrow column is a number with no unit.
	 */
	unit?: string;
	/** Refuse a decimal point. For counts, which cannot be fractional. */
	integerOnly?: boolean;
}

/**
 * A number field.
 *
 * `inputMode="decimal"` rather than `type="number"`, and that is deliberate.
 * `type="number"` brings spinner arrows nobody in a warehouse wants, silently
 * discards a value the browser thinks is malformed — so reading the field back
 * gives `""` for something the person can plainly see — and scrolls the value
 * when a mouse wheel passes over it. `inputMode` gets the numeric keypad on a
 * phone, which is the only part worth having.
 *
 * Money is not this component. An amount is an integer count of minor units and
 * goes through `lib/money`'s parser; a float in a form field is the bug that
 * module exists to prevent.
 */
export function NumberInput({ unit, integerOnly = false, className, ...rest }: NumberInputProps) {
	return (
		<div
			className={cx(
				"flex h-9 items-center rounded-md border border-border-strong bg-bg-surface",
				"focus-within:ring-3 focus-within:ring-ring-halo focus-within:border-ring",
				"has-[input[aria-invalid]]:border-danger-border has-[input[aria-invalid]]:ring-3 has-[input[aria-invalid]]:ring-danger-bg",
				TOUCH_TARGET,
				className,
			)}
		>
			<input
				inputMode={integerOnly ? "numeric" : "decimal"}
				// Digits, one optional decimal point, optional minus. The browser
				// enforces it on a phone keypad and the pattern documents it
				// everywhere else.
				pattern={integerOnly ? "-?[0-9]*" : "-?[0-9]*[.,]?[0-9]*"}
				className={cx(
					"min-w-0 flex-1 bg-transparent px-2.5 text-[13px] text-fg outline-none",
					// Tabular figures, so a column of numbers lines up on the decimal
					// point instead of shimmering as the digits change width.
					"[font-variant-numeric:tabular-nums]",
					"placeholder:text-fg-subtle disabled:cursor-not-allowed disabled:text-fg-subtle",
				)}
				{...rest}
			/>
			{unit !== undefined && (
				<span
					// Decorative: the label already says what the field measures, and
					// "kilograms" announced after every value is noise.
					aria-hidden="true"
					className="border-s border-border px-2.5 text-[13px] text-fg-subtle"
				>
					{unit}
				</span>
			)}
		</div>
	);
}
